import {
  ExtractionFieldValue,
  ExtractionResult,
  ExtractionReviewSchema,
  ExtractionReviewValues,
} from "components/Document/ExtractionReview";
import {
  isWasteContainerTypeCode,
  WASTE_CONTAINER_TYPES,
} from "encamp-shared/src/constants/wasteContainerTypes";
import { safeParseInt } from "encamp-shared/src/tierii/utilities";
import {
  CONTAINERS_MESSAGE,
  isValidContainerCount,
  isValidLineQuantity,
  QUANTITY_MESSAGE,
} from "encamp-shared/src/waste/billOfLadingRules";
import {
  ApplyExtractedBillOfLadingInput,
  BillOfLadingUnit,
  ExtractedBillOfLadingQuery,
  ExtractionStatus,
  Issue,
  IssueLevel,
  ShippingDocumentLineInput,
  WasteKind,
} from "generated-graphql/graphql";
import { toFlexibleNumber } from "util/flexibleNumber";

import { MISSING_SHIP_DATE_WARNING } from "./shippingDocumentDisplay";

/** The repeated group key, which is also the extractor's own array field path. */
const LINES = "lines";

const BILL_UNIT_LABELS: Record<BillOfLadingUnit, string> = {
  [BillOfLadingUnit.Barrels]: "Barrels",
  [BillOfLadingUnit.CubicFeet]: "Cubic feet",
  [BillOfLadingUnit.CubicYards]: "Cubic yards",
  [BillOfLadingUnit.Gallons]: "Gallons",
  [BillOfLadingUnit.Kilograms]: "Kilograms",
  [BillOfLadingUnit.Liters]: "Liters",
  [BillOfLadingUnit.MetricTons]: "Metric tons",
  [BillOfLadingUnit.Pounds]: "Pounds",
  [BillOfLadingUnit.Tons]: "Tons",
};

const UNIT_OPTIONS = Object.values(BillOfLadingUnit).map((value) => ({
  value,
  label: BILL_UNIT_LABELS[value],
}));

/** Container type options, labelled as the Containers dialog shows them. */
const CONTAINER_TYPE_OPTIONS = WASTE_CONTAINER_TYPES.map((type) => ({
  value: type.code,
  label: `${type.label} (${type.code})`,
}));

/**
 * Distinct from `WasteStream.wasteKind`, which process-manifests computes —
 * this is what the document itself declares. "Blank" (an ordinary line the
 * document says nothing special about) is the grid's own "—" option, not one
 * of these.
 */
export const DECLARED_WASTE_KIND_LABELS: Record<WasteKind, string> = {
  [WasteKind.Rcra]: "RCRA",
  [WasteKind.UniversalWaste]: "Universal Waste",
  [WasteKind.UsedOil]: "Used Oil",
  [WasteKind.Pharmaceutical]: "Pharmaceutical",
};

const DECLARED_WASTE_KIND_OPTIONS = Object.values(WasteKind).map((value) => ({
  value,
  label: DECLARED_WASTE_KIND_LABELS[value],
}));

/** The generator EPA ID field, whose registered / in-tenant issues are warnings. */
export const GENERATOR_EPA_ID_KEY = "generatorEpaId";

/**
 * The bill of lading's fields as the reviewer sees them, keyed by the
 * extractor's own field paths — `documentNumber`, `lines[0].description` —
 * so a confidence score addresses a control without any translation, the
 * same posture `WASTE_PROFILE_REVIEW_SCHEMA` (7C) takes.
 *
 * Facility is deliberately not a grid field: decision 4 puts a facility
 * picker in the dialog chrome above the grid instead, since it is confirmed
 * once per document rather than per line.
 */
export const BILL_OF_LADING_REVIEW_SCHEMA: ExtractionReviewSchema = {
  sections: [
    {
      key: "header",
      title: "Document details",
      fields: [
        // Two to a row after the document number: each EPA ID sits beside its
        // name, and the received date beside the ship date.
        { key: "documentNumber", label: "Document number", kind: "text", width: "full" },
        { key: "generatorName", label: "Generator", kind: "text" },
        { key: GENERATOR_EPA_ID_KEY, label: "Generator EPA ID", kind: "text" },
        { key: "transporterName", label: "Transporter", kind: "text" },
        { key: "transporterEpaId", label: "Transporter EPA ID", kind: "text" },
        { key: "destinationName", label: "Destination", kind: "text" },
        { key: "destinationEpaId", label: "Destination EPA ID", kind: "text" },
        {
          key: "shipDate",
          label: "Ship date",
          kind: "text",
          helperText: "YYYY-MM-DD",
          // The save is refused without one, so warn before the reviewer confirms.
          emptyWarning: MISSING_SHIP_DATE_WARNING,
        },
        {
          key: "receivedDate",
          label: "Received date",
          kind: "text",
          helperText: "YYYY-MM-DD",
        },
      ],
    },
  ],
  rowGroups: [
    {
      key: LINES,
      title: "Lines",
      rowNoun: "line",
      /*
       * The description alone. A line restated at a different quantity is the
       * same waste re-weighed at the scale, which is the ordinary case on this
       * paperwork and reads as a changed cell; including quantity would make it
       * a line the document does not have and ask the reviewer about an
       * addition instead of a change.
       *
       * Carrier paperwork has no standard layout and can repeat a description
       * across lines — two drums of the same waste — so this is not a unique
       * key. `pairRecordRows` holds one queue of extracted rows per identity
       * and takes from the front, so two identical descriptions pair with two
       * separate lines rather than both landing on the first. Where the counts
       * differ, the surplus is reported as rows to keep or remove, which is
       * the question the reviewer should be asked.
       */
      identityColumns: ["description"],
      columns: [
        { key: "description", label: "Description", kind: "text" },
        { key: "quantity", label: "Quantity", kind: "number" },
        { key: "unit", label: "Unit", kind: "select", options: UNIT_OPTIONS },
        {
          key: "declaredWasteKind",
          label: "Declared waste kind",
          kind: "select",
          options: DECLARED_WASTE_KIND_OPTIONS,
        },
        { key: "containerCount", label: "Containers", kind: "number" },
        {
          key: "containerType",
          label: "Container type",
          kind: "select",
          options: CONTAINER_TYPE_OPTIONS,
        },
        {
          key: "federalWasteCodes",
          label: "Federal waste codes",
          kind: "text",
          helperText: "Comma-separated",
        },
        {
          key: "stateWasteCodes",
          label: "State waste codes",
          kind: "text",
          helperText: "Comma-separated",
        },
      ],
    },
  ],
};

type Reading = NonNullable<ExtractedBillOfLadingQuery["extractedBillOfLading"]>;

/**
 * The distinct kinds the reading's lines declare, ordered by the enum rather
 * than by where the lines fall, so one document always presents the same set
 * however its lines happen to be sequenced.
 *
 * What the document declares, not what it means for compliance. Whether a kind
 * counts toward generator status is the engine's answer and varies by state and
 * by kind, so stating it here would restate a rule this surface does not own.
 *
 * Reflects the extraction, not the reviewer's live edits: the form state lives
 * inside `ExtractionReviewGrid`, and reaching into it would mean widening that
 * shared component for one caller. The surrounding copy says the reading is what
 * the extractor found, which keeps that honest.
 */
export function declaredKinds(reading: Pick<Reading, "lines"> | null | undefined): WasteKind[] {
  const declared = new Set(
    (reading?.lines ?? [])
      .map((line) => line.declaredWasteKind)
      .filter((kind): kind is WasteKind => kind != null)
  );
  return Object.values(WasteKind).filter((kind) => declared.has(kind));
}

/**
 * The status and error the review grid should show.
 *
 * A reading carrying an error is not reviewable, and the document's own
 * extraction status does not say so: the enrichment job that wrote the payload
 * succeeded, so the document reads as SUCCEEDED while the candidate holds
 * either the extractor's failure or the resolver's "cannot be read back"
 * message for a payload written under a schema it can no longer parse. Showing
 * the document's status alone leaves the reviewer an empty grid and no reason
 * for it.
 */
export function toGridExtractionState(
  reading: Pick<Reading, "error"> | null | undefined,
  document:
    | { extractionStatus?: ExtractionStatus | null; extractionError?: string | null }
    | null
    | undefined
): { status: ExtractionStatus | null | undefined; extractionError: string | null | undefined } {
  if (reading?.error != null) {
    return { status: ExtractionStatus.Failed, extractionError: reading.error };
  }
  return {
    status: document?.extractionStatus,
    extractionError: document?.extractionError,
  };
}

/** Join codes for the grid's single comma-separated text field. */
function joinCodes(codes: readonly string[] | null | undefined): string | null {
  if (!codes || codes.length === 0) return null;
  return codes.join(", ");
}

/**
 * The reading in the shape `ExtractionReviewGrid` consumes.
 *
 * The API keys `fieldConfidences` by field path in the `lines[0].description`
 * form `rowConfidenceKey` builds, but by AI index, so `alignedConfidence`
 * remaps line paths to row positions.
 */
function toValues(values: Reading["values"]): Record<string, ExtractionFieldValue> {
  return {
    documentNumber: values.documentNumber ?? null,
    generatorName: values.generatorName ?? null,
    generatorEpaId: values.generatorEpaId ?? null,
    transporterName: values.transporterName ?? null,
    transporterEpaId: values.transporterEpaId ?? null,
    destinationName: values.destinationName ?? null,
    destinationEpaId: values.destinationEpaId ?? null,
    // `shipDate` is a `Date` scalar on the wire but a free-typed field here —
    // see `ApplyExtractedBillOfLadingInput`'s doc comment on why the reviewer
    // edits it as text rather than through a date picker.
    shipDate: values.shipDate == null ? null : String(values.shipDate),
    receivedDate: values.receivedDate == null ? null : String(values.receivedDate),
  };
}

const EMPTY_LINE: Reading["lines"][number] = {
  extractedIndex: null,
  description: null,
  quantity: null,
  unit: null,
  declaredWasteKind: null,
  containerCount: null,
  containerType: null,
  federalWasteCodes: [],
  stateWasteCodes: [],
};

function toRows(lines: Reading["lines"]): Record<string, Record<string, ExtractionFieldValue>[]> {
  return {
    [LINES]: lines.map((line) => ({
      description: line.description ?? null,
      quantity: line.quantity ?? null,
      unit: line.unit ?? null,
      declaredWasteKind: line.declaredWasteKind ?? null,
      containerCount: line.containerCount ?? null,
      containerType: line.containerType ?? null,
      federalWasteCodes: joinCodes(line.federalWasteCodes),
      stateWasteCodes: joinCodes(line.stateWasteCodes),
    })),
  };
}

/**
 * The baseline rows lined up with the reviewed rows: row i is the AI reading of
 * `lines[i]`, or an empty row for a line the reviewer added. The grid reads
 * `baseline.rows[i]` for row i, but the AI reading skips lines that were removed.
 */
function alignedBaselineRows(
  reading: Reading
): Record<string, Record<string, ExtractionFieldValue>[]> {
  const baseline = toRows(reading.asExtractedLines)[LINES] ?? [];
  const empty = toRows([EMPTY_LINE])[LINES]?.[0] ?? {};
  return {
    [LINES]: reading.lines.map((line) =>
      line.extractedIndex == null ? empty : (baseline[line.extractedIndex] ?? empty)
    ),
  };
}

const LINE_CONFIDENCE_PATH = /^lines\[(\d+)\]\.(.+)$/;

/** Line confidences are keyed by AI index; the grid keys them by row position. */
function alignedConfidence(reading: Reading): ExtractionResult["confidence"] {
  const positionsByIndex = new Map<number, number[]>();
  reading.lines.forEach((line, position) => {
    if (line.extractedIndex == null) return;
    positionsByIndex.set(line.extractedIndex, [
      ...(positionsByIndex.get(line.extractedIndex) ?? []),
      position,
    ]);
  });
  return reading.fieldConfidences
    .filter((entry) => entry.confidence != null)
    .flatMap((entry) => {
      const score = entry.confidence ?? 0;
      const match = LINE_CONFIDENCE_PATH.exec(entry.fieldPath);
      if (!match) return [{ field: entry.fieldPath, score }];
      const positions = positionsByIndex.get(safeParseInt(match[1]) ?? -1) ?? [];
      return positions.map((position) => ({ field: `lines[${position}].${match[2]}`, score }));
    });
}

export function toExtractionResult(reading: Reading): ExtractionResult {
  return {
    // The reviewer's own saved corrections over the model's reading: the
    // resolver runs `applyCandidateCorrections` before returning `values`.
    values: toValues(reading.values),
    rows: toRows(reading.lines),
    // What the model read, uncorrected. The grid shows this as "Suggested: x"
    // beside a field the reviewer has since changed, and offers it back on
    // click. Omitting it makes `extractedBaseline` fall back to `values`, so a
    // second review pass presents a reviewer's own earlier edit as the
    // extraction and the model's reading is gone from the screen.
    extractedValues: toValues(reading.asExtractedValues),
    extractedRows: alignedBaselineRows(reading),
    confidence: alignedConfidence(reading),
  };
}

const UNIT_VALUES: readonly string[] = Object.values(BillOfLadingUnit);
const WASTE_KIND_VALUES: readonly string[] = Object.values(WasteKind);

const isBillUnit = (value: string): value is BillOfLadingUnit => UNIT_VALUES.includes(value);
const isWasteKind = (value: string): value is WasteKind => WASTE_KIND_VALUES.includes(value);

const toUnit = (value: ExtractionFieldValue | undefined): BillOfLadingUnit | null =>
  typeof value === "string" && isBillUnit(value) ? value : null;

const toContainerType = (value: ExtractionFieldValue | undefined): string | null =>
  typeof value === "string" && isWasteContainerTypeCode(value) ? value : null;

const toWasteKind = (value: ExtractionFieldValue | undefined): WasteKind | null =>
  typeof value === "string" && isWasteKind(value) ? value : null;

export function toText(value: ExtractionFieldValue | undefined): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

/**
 * A number passes through, as does a string the whole of which is a number,
 * including one with thousands separators. Confirm refuses bad values before
 * this runs (`lineNumberBlockers`), so null here is only a type guard.
 */
function toQuantity(value: ExtractionFieldValue | undefined): number | null {
  const flexible = toFlexibleNumber(value);
  return typeof flexible === "number" ? flexible : null;
}

/** A whole number of containers. Confirm refuses fractions first; null is only a type guard. */
function toContainerCount(value: ExtractionFieldValue | undefined): number | null {
  const quantity = toQuantity(value);
  return quantity != null && Number.isInteger(quantity) ? quantity : null;
}

/** Blank passes. Non-numeric text is refused; a number must satisfy `isValid`. */
function isUnacceptableNumber(
  value: ExtractionFieldValue | undefined,
  isValid: (n: number) => boolean
): boolean {
  const parsed = toFlexibleNumber(value);
  if (parsed == null) return false;
  return typeof parsed !== "number" || !isValid(parsed);
}

type LineNumberProblem = { index: number; column: string; message: string };

function lineNumberProblems(reviewed: ExtractionReviewValues): LineNumberProblem[] {
  return (reviewed.rows[LINES] ?? []).flatMap((line, index) => {
    const problems: LineNumberProblem[] = [];
    if (isUnacceptableNumber(line.values.containerCount, isValidContainerCount)) {
      problems.push({ index, column: "containerCount", message: CONTAINERS_MESSAGE });
    }
    if (isUnacceptableNumber(line.values.quantity, isValidLineQuantity)) {
      problems.push({ index, column: "quantity", message: QUANTITY_MESSAGE });
    }
    return problems;
  });
}

/** The line cells the apply would refuse, as issues keyed on the cell. */
export function lineNumberIssues(reviewed: ExtractionReviewValues): Issue[] {
  return lineNumberProblems(reviewed).map(({ index, column, message }) => ({
    key: `${LINES}[${index}].${column}`,
    modelName: "WasteLine",
    level: IssueLevel.Critical,
    message,
  }));
}

/** The refusals as `Line N: message`, for stopping a save before the mutation. */
export function lineNumberBlockers(reviewed: ExtractionReviewValues): string[] {
  return lineNumberProblems(reviewed).map(({ index, message }) => `Line ${index + 1}: ${message}`);
}

/**
 * Split a comma-separated codes field back into a clean array.
 *
 * A blank cell is an empty array, never null. The extraction payload types
 * both code fields as non-null arrays and the prompt asks for `[]` when the
 * document shows no codes, so the reviewer has no way to mean "unknown" here —
 * blank means "none printed". Sending null instead would read as a correction
 * away from `[]` on every line that simply has no codes, marking an untouched
 * field as a human edit.
 */
function toCodes(value: ExtractionFieldValue | undefined): string[] {
  if (typeof value !== "string") return [];
  return value
    .split(",")
    .map((code) => code.trim())
    .filter((code) => code !== "");
}

function toLine(row: Record<string, ExtractionFieldValue>): ShippingDocumentLineInput {
  return {
    description: toText(row.description),
    quantity: toQuantity(row.quantity),
    unit: toUnit(row.unit),
    declaredWasteKind: toWasteKind(row.declaredWasteKind),
    containerCount: toContainerCount(row.containerCount),
    containerType: toContainerType(row.containerType),
    federalWasteCodes: toCodes(row.federalWasteCodes),
    stateWasteCodes: toCodes(row.stateWasteCodes),
  };
}

/**
 * The reviewer's confirmed values as an apply input.
 *
 * `shipDate` is passed through as whatever text the reviewer left — trimmed,
 * or null when blank — rather than parsed here. The `Date` scalar's own
 * `parseValue` (`new Date(value)`) is lenient about format, and the field's
 * helper text already steers toward `YYYY-MM-DD`; a string it truly can't
 * read surfaces as a save error naming the field, matching this grid's
 * "server is the validation gate" posture rather than silently nulling a
 * value someone typed.
 */
export function toApplyInput(
  reviewed: ExtractionReviewValues,
  {
    documentId,
    facilityId,
    savedWasteManifestId = null,
    savedWasteManifestVersion = null,
  }: {
    documentId: string;
    facilityId: string;
    savedWasteManifestId?: string | null;
    savedWasteManifestVersion?: string | null;
  }
): ApplyExtractedBillOfLadingInput {
  const { values, rows } = reviewed;
  const lines = rows[LINES] ?? [];
  return {
    documentId,
    // The server refuses the save if the document's saved manifest has changed.
    expectedSavedWasteManifestId: savedWasteManifestId,
    expectedSavedWasteManifestVersion: savedWasteManifestVersion,
    // Sent so the apply records each line's corrections against the extracted
    // line it came from. Without it the server falls back to position, and a
    // line the reviewer deleted or inserted shifts every line below it.
    lineExtractedIndexes: lines.map((line) => line.extractedIndex),
    document: {
      facilityId,
      documentNumber: toText(values.documentNumber),
      generatorName: toText(values.generatorName),
      generatorEpaId: toText(values.generatorEpaId),
      transporterName: toText(values.transporterName),
      transporterEpaId: toText(values.transporterEpaId),
      destinationName: toText(values.destinationName),
      destinationEpaId: toText(values.destinationEpaId),
      shipDate: toText(values.shipDate),
      receivedDate: toText(values.receivedDate),
      lines: lines.map((line) => toLine(line.values)),
    },
  };
}
